import * as projectsEditDialogConfig from '@material-bank/projects-edit-dialog-config';
import {
  useCurrentProject,
  useProjectsProvider,
  useValidateProjectData,
} from '@material-bank/projects-utils';
import {
  shouldTriggerProjectSelectionDialog,
  AnalyticsEventLocation,
  Project,
} from '@mb/lib';
import { ProductSearchMaterial } from '@productSearch/types';
import {
  isUnavailable,
  triggerAssociatedProductsModal,
} from '@productSearch/utils/helpers';
import { openRestrictedItemModal } from '@restrictedItemsModal';
import { useDispatch } from 'react-redux';
import {
  handleTriggerProjectSelectModal,
  redirectGuestToLogin,
} from '../helpers';
import useAddToBag from '../hooks/useAddToBag';
import { setMaterialsInQueue } from '../redux/sliceCreators/commonSlice';
import {
  setIsProjectModalOpen,
  setProjectModalEventLocation,
} from '../redux/sliceCreators/projectsSlice';

interface AddToCartProps {
  product: ProductSearchMaterial;
  shouldTriggerRecModal: boolean;
  onAddToCartClick?: () => void;
  gaEventLocation?: string;
  qvLocation: string;
  atcLocation: string;
  dataLayerAnalytics?: {
    listId: string;
    listName: string;
    eventLocation: string;
  };
  relatedProductData?: {
    id: string;
    title: string;
  };
  isFinish?: boolean;
}

const useAddToBagWithValidations = () => {
  const { isLoading, loadingIds, addToBag } = useAddToBag();
  const { currentProject, setCurrentProjectId } = useCurrentProject();
  const { activeProjects } = useProjectsProvider();
  const { isProjectDataComplete } = useValidateProjectData();

  const dispatch = useDispatch();

  const onProjectSelect = (projectId: number | string) => {
    setCurrentProjectId(projectId);
  };

  const onCreateProjectClick = (params?: {
    analyticsEventLocation?: string;
  }) => {
    const { analyticsEventLocation } = params ?? {};

    dispatch(setIsProjectModalOpen(true));

    if (analyticsEventLocation) {
      dispatch(setProjectModalEventLocation(analyticsEventLocation));
    }
  };

  const handleAddToBag = (props: AddToCartProps) => {
    const selectedProject = {
      id: currentProject?.externalId || 0,
      label: currentProject?.title ?? 'Select project..',
    };

    const {
      product,
      onAddToCartClick,
      shouldTriggerRecModal = false,
      gaEventLocation = 'Quick View',
      dataLayerAnalytics,
      isFinish,
      relatedProductData,
    } = props;

    redirectGuestToLogin();
    if (
      isUnavailable(product) ||
      product.isGeoRestricted ||
      product.isProjectRestricted
    ) {
      openRestrictedItemModal({
        product: product,
        projectId: selectedProject.id,
        subtitle1: product.name,
        subtitle2: product.manufacturer,
      });
      return;
    }

    const analyticsEventData = dataLayerAnalytics
      ? {
          item_list_id: dataLayerAnalytics.listId,
          item_list_name: dataLayerAnalytics.listName,
          event_location: dataLayerAnalytics.eventLocation,
        }
      : undefined;

    if ((product?.associatedFinishProducts || []).length > 0 && !isFinish) {
      // If Finishes - trigger modal to select associated Products
      const productsIds = product?.associatedFinishProducts?.join('|');

      if (shouldTriggerProjectSelectionDialog()) {
        const onLocalProjectSave = (project: Project) => {
          onProjectSelect?.(project.project_id);
          triggerAssociatedProductsModal(
            productsIds,
            {
              id: product.entityId,
              name: product.name,
              manufacturerName: product.manufacturer,
              productImageUrl: product.imageUrl,
            },
            analyticsEventData,
          );
        };

        handleTriggerProjectSelectModal(
          selectedProject?.id,
          AnalyticsEventLocation.QUICK_VIEW,
          onLocalProjectSave,
        );
        return;
      }

      triggerAssociatedProductsModal(
        productsIds,
        {
          id: product?.entityId,
          name: product?.name,
          manufacturerName: product?.manufacturer,
          productImageUrl: product?.imageUrl,
        },
        analyticsEventData,
      );

      return;
    }
    if (
      Object.keys(activeProjects)?.length > 0 &&
      Boolean(selectedProject.id)
    ) {
      if (shouldTriggerProjectSelectionDialog()) {
        const onLocalProjectSave = (project: Project) => {
          onProjectSelect?.(project.project_id);
          onAddToCartClick?.();

          addToBag({
            materials: [product],
            project,
            projectId: project.project_id,
            shouldTriggerModal: shouldTriggerRecModal,
            relatedProductData,
            triggerLocation: gaEventLocation,
            dataLayerAnalytics,
          });
        };

        handleTriggerProjectSelectModal(
          selectedProject?.id,
          gaEventLocation,
          onLocalProjectSave,
        );
        return;
      }

      // If the project is not found, open the create project modal
      if (!currentProject) {
        window.dispatchEvent(
          new CustomEvent('open-create-project-modal', {
            detail: {
              eventLocation: dataLayerAnalytics?.eventLocation || 'Search',
            },
          }),
        );

        dispatch(setMaterialsInQueue([product]));

        return;
      }

      // Complete any missing project data
      if (!isProjectDataComplete(currentProject)) {
        projectsEditDialogConfig.openProjectEditDialog({
          projectId: selectedProject.id,
          successCallback: async () => {
            onAddToCartClick?.();

            addToBag({
              materials: [product],
              projectId: selectedProject.id,
              relatedProductData,
              triggerLocation: gaEventLocation,
              shouldTriggerModal: shouldTriggerRecModal,
              dataLayerAnalytics,
            });
          },
        });

        return;
      }

      onAddToCartClick?.();

      addToBag({
        materials: [product],
        projectId: selectedProject.id,
        relatedProductData,
        triggerLocation: gaEventLocation,
        shouldTriggerModal: shouldTriggerRecModal,
        dataLayerAnalytics,
      });
    } else {
      onCreateProjectClick?.();
      dispatch(setMaterialsInQueue([product]));
    }
  };

  return { isLoading, loadingIds, handleAddToBag };
};

export default useAddToBagWithValidations;
